"use client";

import { Dialog as CommandDialogPrimitive } from "@base-ui/react/dialog";
import * as React from "react";
import { AncestorLayerContext } from "../../lib/backdrop-layer";
import { cva, type VariantProps } from "../../lib/cva";
import { Icon } from "./icon";
import { useKeyboardHints } from "../../hooks/use-keyboard-hints";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";
import {
  useOptionalAutoFocus,
  useTouchAwareInitialFocus,
} from "../../hooks/use-open-focus";
import {
  Autocomplete,
  AutocompleteCollection,
  AutocompleteEmpty,
  AutocompleteGroup,
  AutocompleteGroupLabel,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompleteSeparator,
} from "./autocomplete";

export const CommandDialog: typeof CommandDialogPrimitive.Root =
  CommandDialogPrimitive.Root;

export const CommandDialogPortal: typeof CommandDialogPrimitive.Portal =
  CommandDialogPrimitive.Portal;

export function CommandDialogTrigger(
  props: CommandDialogPrimitive.Trigger.Props,
): React.ReactElement {
  return (
    <CommandDialogPrimitive.Trigger
      data-slot="command-dialog-trigger"
      {...props}
    />
  );
}

export function CommandDialogBackdrop({
  className,
  ...props
}: CommandDialogPrimitive.Backdrop.Props): React.ReactElement {
  // Base UI drops a nested layer's backdrop on the assumption an ancestor
  // dimmed the page. Every modal layer paints its own scrim instead, so the
  // palette dims whatever layer it opens over. See AncestorLayerContext.
  return (
    <CommandDialogPrimitive.Backdrop
      forceRender
      className={cn(
        "fixed inset-0 z-50 bg-black/32 backdrop-blur-sm transition-all duration-base data-ending-style:opacity-0 data-starting-style:opacity-0",
        className,
      )}
      data-slot="command-dialog-backdrop"
      {...props}
    />
  );
}

export function CommandDialogViewport({
  className,
  ...props
}: CommandDialogPrimitive.Viewport.Props): React.ReactElement {
  return (
    <CommandDialogPrimitive.Viewport
      className={cn(
        "fixed inset-0 z-50 flex flex-col items-center px-4 py-[max(--spacing(4),4vh)] sm:py-[10vh]",
        className,
      )}
      data-slot="command-dialog-viewport"
      {...props}
    />
  );
}

export const commandDialogPopupVariants = cva(
  // The `before:` veil is the coss two-tone: a wash over the popup that
  // the chrome zones (input header, scope chips, footer) show through,
  // while the opaque CommandPanel — later in tree order — pops clean on
  // top of it. Light paints full `--muted`; dark paints `--background`
  // instead, because the compressed dark ramp holds `--muted` under 1 L
  // above `--popover`, where a muted veil computes to no visible step.
  // `--background` sits about 4 L under `--popover` — the page-to-popup
  // step — so the frame reads as the page showing through and the panel
  // as the one popup resting on it, the same reading every CommandDialog
  // (global search, bulk More) shares. An alpha wash was rejected: black/10
  // lands only ~2 L under and the recess barely registers, while black/25
  // drops the frame past the whole dark ramp and the panel reads brighter
  // than every other popup even though it is the same `--popover` fill.
  // The footer keeps its own bright ink so the legend still reads on the
  // veiled zone.
  "relative row-start-2 flex min-h-0 w-full min-w-0 max-w-xl -translate-y-[calc(1.25rem*var(--nested-dialogs))] scale-[calc(1-0.1*var(--nested-dialogs))] flex-col rounded-xl border bg-popover not-dark:bg-clip-padding text-popover-foreground opacity-[calc(1-0.1*var(--nested-dialogs))] shadow-popover outline-none transition-[scale,opacity,translate] duration-base ease-in-out will-change-transform before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-xl)-1px)] before:bg-muted dark:before:bg-background before:shadow-[0_1px_--theme(--color-black/4%)] data-nested:data-ending-style:translate-y-8 data-nested:data-starting-style:translate-y-8 data-nested-dialog-open:origin-top data-ending-style:scale-98 data-starting-style:scale-98 data-ending-style:opacity-0 data-starting-style:opacity-0 **:data-[slot=scroll-area-viewport]:data-has-overflow-y:pe-1 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
  {
    defaultVariants: {
      fixedHeight: false,
    },
    variants: {
      // The height cap: a fixed height is capped at 80dvh (short screens)
      // and never shrinks; the default keeps the content cap (max-h-105).
      fixedHeight: {
        true: "max-h-[80dvh] shrink-0",
        false: "max-h-105",
      },
    },
  },
);

export function CommandDialogPopup({
  className,
  children,
  portalProps,
  fixedHeight,
  style,
  initialFocus,
  ref,
  ...props
}: CommandDialogPrimitive.Popup.Props & Omittable<{
  portalProps?: CommandDialogPrimitive.Portal.Props;
  /** Pin the popup to an exact px height (capped at 80dvh) so it never resizes
   *  with content — the body's `grow` panel fills, the list scrolls inside.
   *  Omit for the default content-sized dialog (capped at max-h-105). Set on
   *  the popup's own `style` here rather than a consumer className so the base
   *  `max-h-105` cap can't clamp it and no class-merge order can drop it. */
  fixedHeight?: number;
}>): React.ReactElement {
  // A coarse-pointer open lands on the popup, not on the first field inside
  // it (Base UI reads the click, which WebKit stamps "mouse" for a finger);
  // a field that claimed focus by its own `autoFocus` keeps it.
  const touchFocus = useTouchAwareInitialFocus<HTMLDivElement>(ref);
  return (
    <CommandDialogPortal {...portalProps}>
      <CommandDialogBackdrop />
      {/* A modal layer: a drawer opened from inside paints its own scrim over
          this popup (only a drawer-on-drawer stack withholds one). */}
      <AncestorLayerContext.Provider value="modal">
        <CommandDialogViewport>
          <CommandDialogPrimitive.Popup
            className={cn(
              commandDialogPopupVariants({ fixedHeight: fixedHeight != null }),
              className,
            )}
            style={
              fixedHeight != null ? { height: fixedHeight, ...style } : style
            }
            // Portalled, so it inherits no surface scope — names its own, and
            // descendant `border-border` draws the popover-tuned hairline.
            data-on="popover"
            data-slot="command-dialog-popup"
            initialFocus={initialFocus ?? touchFocus.initialFocus}
            ref={touchFocus.ref}
            {...props}
          >
            {children}
          </CommandDialogPrimitive.Popup>
        </CommandDialogViewport>
      </AncestorLayerContext.Provider>
    </CommandDialogPortal>
  );
}

export function Command({
  autoHighlight = "always",
  keepHighlight = true,
  ...props
}: React.ComponentProps<typeof Autocomplete>): React.ReactElement {
  return (
    <Autocomplete
      autoHighlight={autoHighlight}
      inline
      keepHighlight={keepHighlight}
      open
      {...props}
    />
  );
}

export const commandInputWrapperVariants = cva("", {
  compoundVariants: [
    // The sm input is the compact inline-bar case: the wrapper sheds most of
    // its horizontal inset so the start addon sits near the container edge
    // instead of double-padding away from it.
    { className: "px-1", inset: false, sm: true },
    { className: "px-2.5", inset: false, sm: false },
  ],
  defaultVariants: {
    inset: false,
    sm: false,
  },
  variants: {
    inset: {
      true: "px-2 pt-2",
      false: "py-1.5",
    },
    sm: {
      true: "",
      false: "",
    },
  },
});

export const commandInputVariants = cva("", {
  defaultVariants: {
    inset: false,
  },
  variants: {
    inset: {
      false:
        "border-transparent! bg-transparent! shadow-none before:hidden has-focus-visible:ring-0",
    },
  },
});

export function CommandInput({
  className,
  placeholder = undefined,
  size,
  inset = false,
  // Overridable: `startAddon={null}` drops the glyph (and its input inset)
  // for surfaces where the context already says "search".
  startAddon = <Icon name="search" />,
  // Unset, the caret lands where a hardware keyboard is implied and waits
  // for its own tap on a coarse pointer (`useOptionalAutoFocus`); a palette
  // whose whole point is typing passes `autoFocus` itself.
  autoFocus,
  ...props
}: React.ComponentProps<typeof AutocompleteInput> & Omittable<{
  /** The coss Combobox treatment (the selector-popover grammar): a real
   *  bordered, rounded input floating inset in the popup instead of the
   *  flush borderless ⌘K header row. No bottom padding — the list below
   *  carries its own 8px gutter, so input-to-first-row stays one 8px step. */
  inset?: VariantProps<typeof commandInputVariants>["inset"];
}>): React.ReactElement {
  const resolvedSize = size ?? (inset ? "default" : "lg");
  const resolvedAutoFocus = useOptionalAutoFocus(autoFocus);
  return (
    <div
      className={commandInputWrapperVariants({
        inset,
        sm: resolvedSize === "sm",
      })}
    >
      <AutocompleteInput
        // `Command` runs its Autocomplete inline and always open, and an
        // inline list publishes no expanded state on the combobox it names —
        // the required attribute has to come from the one composition that
        // knows the list is showing. A caller driving `open` itself passes
        // its own value through.
        aria-expanded
        autoFocus={resolvedAutoFocus}
        className={cn(commandInputVariants({ inset }), className)}
        placeholder={placeholder}
        size={resolvedSize}
        startAddon={startAddon}
        {...props}
      />
    </div>
  );
}

export function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteList>): React.ReactElement {
  return (
    <AutocompleteList
      // The overflow gutter widens past AutocompleteList's 12px: command
      // panels are wide surfaces and rows read cramped against the scrollbar.
      className={cn(
        "not-empty:scroll-py-2 not-empty:p-2 in-data-has-overflow-y:pe-4",
        className,
      )}
      data-slot="command-list"
      {...props}
    />
  );
}

export function CommandEmpty({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteEmpty>): React.ReactElement {
  return (
    <AutocompleteEmpty
      className={cn("not-empty:py-6", className)}
      data-slot="command-empty"
      {...props}
    />
  );
}

export function CommandPanel({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      // A flex column that grows: the popup caps its height, and the
      // ScrollArea inside CommandList only scrolls when this panel hands it a
      // definite height instead of letting content push the popup taller.
      className={cn(
        // `flex grow flex-col` so a fixedHeight popup's panel takes the slack
        // instead of the popup opening tall around a content-height panel.
        // The footer seam keys off `~` (any LATER sibling), not `+`: Base UI's
        // Autocomplete renders a hidden input between the panel and whatever
        // follows it, so strict adjacency never matches and the panel keeps a
        // rounded bottom it should have handed to the footer.
        "relative -mx-px not-has-[~[data-slot=command-footer]]:-mb-px flex min-h-0 grow flex-col rounded-t-lg not-has-[~[data-slot=command-footer]]:rounded-b-xl border border-b-0 bg-popover bg-clip-padding shadow-xs/5 [clip-path:inset(0_1px)] not-has-[~[data-slot=command-footer]]:[clip-path:inset(0_1px_1px_1px_round_0_0_calc(var(--radius-xl)-1px)_calc(var(--radius-xl)-1px))] before:pointer-events-none before:absolute before:inset-0 before:rounded-t-[calc(var(--radius-lg)-1px)] **:data-[slot=scroll-area-scrollbar]:mt-2",
        className,
      )}
      {...props}
    />
  );
}

export function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteGroup>): React.ReactElement {
  return (
    <AutocompleteGroup
      className={className}
      data-slot="command-group"
      {...props}
    />
  );
}

export function CommandGroupLabel({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteGroupLabel>): React.ReactElement {
  return (
    <AutocompleteGroupLabel
      // One rung quieter than the stock muted label: on a command panel the
      // section names are wayfinding, not content, and muted competes with
      // the rows' own secondary text.
      className={cn("text-faint-foreground", className)}
      data-slot="command-group-label"
      {...props}
    />
  );
}

export const CommandCollection = AutocompleteCollection;

export function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteItem>): React.ReactElement {
  return (
    <AutocompleteItem
      className={cn("py-1.5", className)}
      data-slot="command-item"
      {...props}
    />
  );
}

export const commandSeparatorVariants = cva("my-2", {
  variants: {
    bleed: {
      // The list's 8px gutter, plus the sheet inset when the popup presents
      // as a phone sheet (`DrawerInsetPanel`; 0px in a popup).
      true: "-mx-[calc(--spacing(2)+var(--sheet-inset,0px))]",
    },
  },
});

export function CommandSeparator({
  className,
  bleed = false,
  ...props
}: React.ComponentProps<typeof AutocompleteSeparator> & Omittable<{
  /** Run edge to edge across the popup: cancels CommandList's 8px gutter
   *  (the two values are one grammar — retune them together). */
  bleed?: VariantProps<typeof commandSeparatorVariants>["bleed"];
}>): React.ReactElement {
  return (
    <AutocompleteSeparator
      className={cn(commandSeparatorVariants({ bleed }), className)}
      data-slot="command-separator"
      {...props}
    />
  );
}

/** The row's key binding. Nothing on a device with no keyboard to press it. */
export function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"kbd">): React.ReactElement | null {
  if (!useKeyboardHints()) return null;
  return (
    <kbd
      className={cn(
        "ms-auto font-medium font-sans text-muted-foreground/72 text-xs tracking-widest",
        className,
      )}
      data-slot="command-shortcut"
      {...props}
    />
  );
}

export function CommandFooter({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 rounded-b-[calc(var(--radius-xl)-1px)] border-t px-5 py-3 text-muted-foreground text-xs",
        className,
      )}
      data-slot="command-footer"
      {...props}
    />
  );
}
